import _ from 'lodash';
import { getReportRequestPath } from '@/views/reports/report-route-behavior';
import { normalizeDateTimeQueryParams } from '@/views/reports/normalize-datetime-query-value';

export type SimpleReportParamsContext = {
  tableQueryForCurrentTab: Record<string, unknown>;
  tableDimensions: {
    mainColumn: string;
    additionalColumns?: string[];
  };
  metrics: string[];
  dimensions: Record<string, { mainColumn: string }>;
  treeLevel: number;
  getFilters: () => Record<string, unknown>;
  reportMetricsName: (key: string) => string;
};

export type SimpleReportParamsResult = {
  params?: Record<string, unknown>;
  appendMetrics: Array<{ text: string; value: string }>;
};

export const buildSimpleReportParams = (
  context: SimpleReportParamsContext,
): SimpleReportParamsResult => {
  const { view } = context.tableQueryForCurrentTab;

  const params: Record<string, unknown> = normalizeDateTimeQueryParams({
    ...context.getFilters(),
    formatResponse: 1,
  });

  let dimensions: string[] = context.tableDimensions.additionalColumns
    ?.concat([context.tableDimensions.mainColumn])
    ?.concat(context.metrics);

  if (!dimensions) {
    return { params: undefined, appendMetrics: [] };
  }

  let appendMetrics: Array<{ text: string; value: string }> = [];

  if (view) {
    const levels = (context.tableQueryForCurrentTab.treeLevels as string[] | undefined)?.filter(
      (level) => level,
    );

    if (view === 'table' && levels?.length) {
      params.groupBy = [params.groupBy, ...levels];

      const viewColumns = levels.map((level) => context.dimensions[level].mainColumn);
      appendMetrics = viewColumns.map((key) => ({
        text: context.reportMetricsName(key),
        value: key,
      }));

      params.columns = _.uniq((params.columns as string[]).concat(viewColumns));
      dimensions = dimensions.concat(viewColumns);
    }
  }

  if (!dimensions.includes(params.orderBy as string)) {
    params.orderBy = context.tableDimensions.mainColumn;
  }

  if (context.treeLevel > 0) {
    delete params.searchBy;
    delete params.search;
    delete params.marks;
    delete params.marksFilterMode;
    delete params.tagsLogicalRelation;
    delete params.textLogicalRelation;
    delete params.tags;

    params.limit = 20;
    params.offset = 0;
  }

  return { params, appendMetrics };
};

export type ReportRequestParamsContext = {
  pickedFilters: Record<string, unknown> | undefined;
  reportQuery: Record<string, unknown>;
  previousFilter?: Bemob.ReportQueryDrill[];
  compareReportEnabled: boolean;
  selectedWorkspaces: string[];
  workspacesCount: number;
  reportBoostEnabled: boolean;
  metrics: string[];
  tableMainColumn: string;
};

export const buildReportRequestParams = (context: ReportRequestParamsContext) => {
  const params = _.cloneDeep(context.pickedFilters) as Record<string, unknown>;

  if (params.columns && (params.columns as string[]).length === 1) {
    (params.columns as string[]).push((params.columns as string[])[0]);
  }

  const filter: Bemob.ReportQueryDrill[] = [];
  const reportQuery = context.reportQuery;

  if (reportQuery.filterTables && reportQuery.filterValues) {
    filter.push({
      filterTables: reportQuery.filterTables as string,
      filterValues: reportQuery.filterValues as string,
      filterOptions: reportQuery.filterOptions as Bemob.ReportQueryDrill['filterOptions'],
    });
  }

  if (!context.compareReportEnabled) {
    delete params.compareDate;
    delete params.compareFrom;
    delete params.compareTo;
    delete params.compareEnabled;
  }

  if (context.selectedWorkspaces.length === context.workspacesCount) {
    delete params.workspaceIds;
  } else {
    params.workspaceIds = context.selectedWorkspaces;
  }

  if (reportQuery.drill) {
    filter.push(...(reportQuery.drill as Bemob.ReportQueryDrill[]));
  }

  if (context.previousFilter) {
    filter.push(...context.previousFilter);
  }

  if (filter.length) {
    params.filterTables = filter.map((item) => item.filterTables);
    params.filterValues = filter.map((item) => item.filterValues);
    params.filterOptions = filter.map((item) => item.filterOptions);
  }

  if (context.reportBoostEnabled) {
    params.columns = (params.columns as string[]).filter((item) => {
      params.orderBy = context.tableMainColumn;
      return !context.metrics.includes(item);
    });
  }

  return params;
};

export const getReportRequestLink = (routeName: string | symbol | null | undefined) => {
  return getReportRequestPath(routeName);
};

export const isStaleReportResponse = (
  params: Record<string, unknown>,
  isReport: boolean,
  currentReportType: string,
) => {
  return !isReport && params.groupBy !== currentReportType;
};
